// project imports
import { Theme } from '@mui/material/styles';

export default function componentStyleOverrides(theme: Theme, borderRadius: number, outlinedFilled: boolean) {
  const mode = theme.palette.mode;
  const bgColor = mode === 'dark' ? theme.palette.dark[800] : theme.palette.grey[50];
  const menuSelectedBack = mode === 'dark' ? theme.palette.primary.main + 15 : theme.palette.primary.light;
  const menuSelected = mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark;

  return {
    MuiCssBaseline: {
      styleOverrides: {
        ...(mode === 'dark' && {
          '*::-webkit-scrollbar': {
            width: 8,
            height: 8
          },
          '*::-webkit-scrollbar-track': {
            background: theme.palette.background.default
          },
          '*::-webkit-scrollbar-thumb': {
            background: theme.palette.divider,
            borderRadius: 8
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: theme.palette.dark.main
          },
          body: {
            scrollbarColor: '#222f44 #0f1729',
            scrollbarWidth: 'thin'
          }
        })
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: '8px',
          padding: '8px 16px'
        }
      }
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        },
        rounded: {
          borderRadius: `${borderRadius}px`
        }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          color: theme.palette.text.dark,
          padding: '24px'
        },
        title: {
          fontSize: '1.125rem'
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px'
        }
      }
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '24px'
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          alignItems: 'center'
        },
        outlined: {
          border: '1px dashed'
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: theme.palette.text.primary,
          paddingTop: '10px',
          paddingBottom: '10px',
          '&.Mui-selected': {
            color: menuSelected,
            backgroundColor: menuSelectedBack,
            '&:hover': {
              backgroundColor: menuSelectedBack
            },
            '& .MuiListItemIcon-root': {
              color: menuSelected
            }
          },
          '&:hover': {
            backgroundColor: menuSelectedBack,
            color: menuSelected,
            '& .MuiListItemIcon-root': {
              color: menuSelected
            }
          }
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: theme.palette.text.primary,
          minWidth: '36px'
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: theme.palette.text.dark
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: theme.palette.text.dark,
          padding: '8px 16px',
          '&::placeholder': {
            color: theme.palette.text.secondary,
            fontSize: '0.875rem'
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: outlinedFilled ? bgColor : 'transparent',
          borderRadius: `${borderRadius}px`,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: mode === 'dark' ? theme.palette.text.primary + 28 : theme.palette.grey[400]
          },
          '&:hover $notchedOutline': {
            borderColor: theme.palette.primary.light
          },
          '&.MuiInputBase-multiline': {
            padding: 1
          },
          '& .Mui-disabled': {
            backgroundColor: mode === 'dark' ? theme.palette.background.default : theme.palette.grey[100],
            borderColor: mode === 'dark' ? theme.palette.divider : theme.palette.grey[100]
          },
          '&.Mui-disabled': {
            backgroundColor: mode === 'dark' ? theme.palette.background.default : theme.palette.grey[100],
            borderColor: mode === 'dark' ? theme.palette.divider : theme.palette.grey[100]
          }
        },
        input: {
          fontWeight: 500,
          background: outlinedFilled ? bgColor : 'transparent',
          padding: '8px 16px',
          borderRadius: `${borderRadius}px`,
          // Dark mode: Chrome paints autofilled fields with its own background (it ignores the
          // `background` above), which left an autofilled login input lighter than its own
          // show/hide eye adornment. Repaint autofill to the normal input bg so they match.
          // `!important` so this deterministically beats any other equal-specificity
          // autofill override (there is an emotion-injected one that paints the field blue).
          ...(mode === 'dark' && {
            '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
              WebkitBoxShadow: `0 0 0 100px ${bgColor} inset !important`,
              WebkitTextFillColor: theme.palette.text.primary,
              caretColor: theme.palette.text.primary,
              borderRadius: `${borderRadius}px`
            }
          }),
          '&.MuiInputBase-inputSizeSmall': {
            padding: '8px 16px',
            '&.MuiInputBase-inputAdornedStart': {
              paddingLeft: 0
            }
          }
        },
        inputAdornedStart: {
          paddingLeft: 4
        },
        notchedOutline: {
          borderRadius: `${borderRadius}px`
        }
      }
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: mode === 'dark' ? theme.palette.text.primary + 50 : theme.palette.grey[300]
          }
        },
        mark: {
          backgroundColor: theme.palette.background.paper,
          width: '4px'
        },
        valueLabel: {
          color: mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.light
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            padding: '8px',
            '& .MuiAutocomplete-input': {
              padding: '0 8px'
            }
          },
          '& .MuiAutocomplete-tag': {
            backgroundColor: theme.palette.primary[200],
            borderRadius: 8,
            color: theme.palette.primary.main,
            fontWeight: 500,
            padding: 1,
            '.MuiChip-deleteIcon': {
              color: 'inherit'
            }
          },
          '& .MuiInputBase-input': {
            backgroundColor: 'inherit'
          },
          '& .Mui-disabled': {
            backgroundColor: mode === 'dark' ? theme.palette.dark[800] : theme.palette.grey[100],
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? theme.palette.divider : theme.palette.grey[100]
            }
          }
        },
        popper: {
          borderRadius: `${borderRadius}px`,
          boxShadow: '0px 8px 10px -5px rgb(0 0 0 / 20%), 0px 16px 24px 2px rgb(0 0 0 / 14%), 0px 6px 30px 5px rgb(0 0 0 / 12%)'
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem'
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: theme.palette.divider,
          opacity: mode === 'dark' ? 0.2 : 1
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          padding: '8px 16px',
          '&:focus': {
            backgroundColor: 'transparent'
          }
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {}
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          color: mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.dark,
          background: mode === 'dark' ? theme.palette.text.primary : theme.palette.primary[200]
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          backgroundColor: theme.palette.primary[100],
          color: theme.palette.primary.main,
          fontSize: '0.75rem',
          minWidth: 'auto',
          '&.MuiChip-deletable .MuiChip-deleteIcon': {
            color: 'inherit'
          }
        }
      }
    },
    MuiTimelineContent: {
      styleOverrides: {
        root: {
          color: theme.palette.text.dark,
          fontSize: '16px'
        }
      }
    },
    MuiTreeItem: {
      styleOverrides: {
        label: {
          marginTop: 14,
          marginBottom: 14
        }
      }
    },
    MuiTimelineDot: {
      styleOverrides: {
        root: {
          boxShadow: 'none'
        }
      }
    },
    MuiInternalDateTimePickerTabs: {
      styleOverrides: {
        tabs: {
          backgroundColor: mode === 'dark' ? theme.palette.dark[900] : theme.palette.primary.light,
          '& .MuiTabs-flexContainer': {
            borderColor: mode === 'dark' ? theme.palette.text.primary + 20 : theme.palette.primary[200]
          },
          '& .MuiTab-root': {
            color: mode === 'dark' ? theme.palette.text.secondary : theme.palette.grey[900]
          },
          '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.primary.dark
          },
          '& .Mui-selected': {
            color: theme.palette.primary.dark
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        flexContainer: {
          borderBottom: '1px solid',
          borderColor: mode === 'dark' ? theme.palette.text.primary + 20 : theme.palette.grey[200]
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          // Dark-only. Two things dim tab labels to gray in dark:
          //  1) MUI's default unselected label color is text.secondary (#94a3b8) — lift to text.primary.
          //  2) MUI dims `textColorInherit` UNSELECTED tabs with `opacity: 0.6`, so even white text
          //     renders as gray. Force opacity:1 so the (now white) label shows at full strength.
          // `.Mui-selected` (primary.main) and `.Mui-disabled` (disabledOpacity) are more specific and
          // are left untouched. Light keeps MUI's defaults byte-identical.
          ...(mode === 'dark' && { color: theme.palette.text.primary, opacity: 1 })
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          padding: 0
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: mode === 'dark' ? theme.palette.text.primary + 15 : theme.palette.grey[200],
          '&.MuiTableCell-head': {
            fontSize: '0.875rem',
            color: mode === 'dark' ? theme.palette.text.primary : theme.palette.grey[900],
            fontWeight: 500
          }
        }
      }
    },
    MuiDateTimePickerToolbar: {
      styleOverrides: {
        timeDigitsContainer: {
          alignItems: 'center'
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          color: theme.palette.background.paper,
          background: theme.palette.text.primary
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
          '+ .MuiDialogContent-root': {
            paddingTop: 3
          }
        }
      }
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          margin: '3px'
        }
      }
    },
    MuiDataGrid: {
      defaultProps: {
        rowHeight: 54
      },
      styleOverrides: {
        root: {
          border: 'none',
          '& .MuiFormControl-root>.MuiInputBase-root': {
            backgroundColor: theme.palette.background.default + ' !important',
            borderColor: theme.palette.mode === 'dark' ? theme.palette.divider + 20 + ' !important' : theme.palette.divider + ' !important'
          }
        },
        row: {
          borderBottom: `1px solid ${mode === 'dark' ? theme.palette.divider + 20 : theme.palette.divider}`
        },
        columnHeader: {
          paddingLeft: 24,
          paddingRight: 24,
          borderBottom: `1px solid ${mode === 'dark' ? theme.palette.divider + 20 : theme.palette.divider}`
        },
        columnHeaderCheckbox: {
          paddingLeft: 0,
          paddingRight: 0
        },
        cellCheckbox: {
          paddingLeft: 0,
          paddingRight: 0
        },
        cell: {
          border: 'none',
          paddingLeft: 24,
          paddingRight: 24,
          '&.MuiDataGrid-cell--withRenderer > div ': {
            ...(theme.palette.mode === 'dark' && {
              color: theme.palette.grey[50]
            }),
            ' > .high': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.success.dark : theme.palette.success.light
            },
            '& > .medium': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.warning.light
            },
            '& > .low': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.error.dark : theme.palette.error.light
            }
          }
        },
        columnsContainer: {
          borderColor: mode === 'dark' ? theme.palette.divider + 20 : theme.palette.divider
        },
        columnSeparator: {
          borderColor: mode === 'dark' ? theme.palette.divider + 20 : theme.palette.divider
        },
        withBorderColor: {
          borderColor: mode === 'dark' ? theme.palette.divider + 20 : theme.palette.divider
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          marginTop: theme.spacing(1),
          minWidth: 210,
          color: theme.palette.mode === 'light' ? theme.palette.grey[600] : theme.palette.grey[300],
          boxShadow:
            'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
          '& .MuiMenu-list': {
            padding: '4px 0'
          },
          '& .MuiMenuItem-root': {
            '& > svg': {
              color: theme.palette.grey[600],
              marginRight: theme.spacing(1)
            },
            '& .MuiSvgIcon-root': {
              fontSize: 18,
              color: theme.palette.grey[600],
              marginRight: theme.spacing(1.5)
            },
            '&:active': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.grey[50]
            }
          }
        }
      }
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          // bare skeletons default to a light shimmer (alpha white) in dark, which
          // reads as light boxes; anchor them to a subtle dark surface instead.
          ...(mode === 'dark' && { backgroundColor: theme.palette.dark[800] })
        }
      }
    }
  };
}
